// Shared display formatters for the job-costing UI.

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

export const formatCurrency = (value: number | null | undefined): string =>
  currencyFormatter.format(Number(value ?? 0));

// Cents-precision variant, used only where whole-dollar rounding would lie (see below).
const centsFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Ledger amounts (cost-line rows + their category subtotals) — WEBAPP-BUG-018.
 *
 * Whole dollars like everywhere else, EXCEPT when rounding would render a non-zero
 * amount as "$0" (|value| < 0.5), which reads as a broken/empty row. Those fall back
 * to cents. Deliberately narrow: ~71% of live cost lines carry cents, so formatting
 * every fractional amount to 2dp would restyle most of the product to fix 75 rows.
 * Headline cards, charts, and profitability rollups keep plain formatCurrency.
 */
export const formatLineAmount = (value: number | null | undefined): string => {
  const n = Number(value ?? 0);
  return n !== 0 && Math.abs(n) < 0.5 ? centsFormatter.format(n) : currencyFormatter.format(n);
};

/** Gross margin is null when revenue is 0 — show an em dash, not "0%". */
export const formatMargin = (pct: number | null | undefined): string =>
  (pct === null || pct === undefined ? '—' : `${pct.toFixed(1)}%`);

/**
 * Budget variance (MONGOOSE-FR-007): + = under budget, − = over budget. Null/undefined
 * (no budget set) → em dash, not "$0".
 */
export const formatSignedCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  const abs = currencyFormatter.format(Math.abs(n));
  if (n > 0) return `+${abs}`;
  if (n < 0) return `-${abs}`;
  return abs;
};

/** Signed variance percentage (MONGOOSE-FR-007). Null (no budget) → em dash. */
export const formatSignedPercent = (pct: number | null | undefined): string => {
  if (pct === null || pct === undefined) return '—';
  const n = Number(pct);
  const abs = `${Math.abs(n).toFixed(1)}%`;
  if (n > 0) return `+${abs}`;
  if (n < 0) return `-${abs}`;
  return abs;
};

// Deep link to the opportunity in RocketCRM/GHL (mirrors OpportunitySearch).
const GHL_BASE_URL = 'https://crm.rocketplantech.com/v2/location';

export const buildGhlOpportunityUrl = (locationId: string, opportunityId: string): string =>
  `${GHL_BASE_URL}/${locationId}/opportunities/${opportunityId}?tab=Opportunity+Details`;

export const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
};
